import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TaskList } from '../TaskList'
import { taskToEntryData } from '../../utils/taskToEntryData'
import type { TaskEntryData } from '../TaskEntry'
import type { ExternalSelectionProps } from '../../hooks/useTaskListInteraction'
import type { CompletedToggleProps } from './CompletedToggle.types'
import './CompletedToggle.scss'

/** „16:42" — die Uhrzeit, zu der abgehakt wurde. */
function formatCompletedTime(completedAt: Date | null): string {
  if (!completedAt) return ''
  const d = completedAt instanceof Date ? completedAt : new Date(completedAt)
  if (Number.isNaN(d.getTime())) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/**
 * Der **Geschafft-Schalter** (CONTEXT.md) am Fuß einer **Aufgabenliste**:
 * „3 geschaffte Aufgaben einblenden", und dahinter die erledigten Aufgaben dieser
 * Liste — zuletzt Erledigtes oben, mit der Uhrzeit.
 *
 * **Dahinter stehen gewöhnliche Aufgabenzeilen** (entschieden 2026-09-12): dieselbe
 * `TaskList` wie oben, also auswählbar, mit Pfeiltasten erreichbar, aufklappbar, mit
 * ihren **Marken**. Bis dahin war es eine schlichte Zeile ohne all das — „ein Rückblick,
 * keine Arbeitsfläche". Geblieben ist davon zweierlei: **kein Ziehen** (der Schalter
 * sortiert nach `completedAt`, und eine Ordnung, die man nicht sieht, kann man nicht
 * sortieren — ADR 0019; er steht deshalb außerhalb des `SortableContext`), und **das
 * dreizeilige Menü** von **Geschafft** statt der zehn Zeilen einer offenen Aufgabe.
 *
 * **Die Auswahl gehört der Seite, nicht dem Schalter**: `externalSelection` kommt aus
 * derselben Navigation wie die Liste darüber. So läuft die Pfeiltaste aus der Liste in
 * den Haufen hinein, und `⇧`/`⌘` wählen über die Grenze. Zugeklappt steht hier nichts
 * im Baum und nichts in der Navigation.
 *
 * **Archivierte Abschnitte** (#193) stehen darunter gruppiert: erst das übrige Erledigte flach,
 * dann je Abschnitt sein Kopf (`renderArchivedSection`) und seine erledigten Aufgaben. Das sind
 * mehrere Listen, die sich die eine Auswahl der Seite teilen — die Köpfe sind keine Stationen
 * der Pfeiltasten.
 *
 * @example
 * <CompletedToggle
 *   tasks={completedTasks}
 *   open={completedOpen}
 *   onOpenChange={setCompletedOpen}
 *   onRestore={handleRestoreCompleted}
 *   onDelete={id => handleDeleteTask(new Set([id]))}
 *   externalSelection={navigation.getExternalSelection(completedTasks.map(t => t.id))}
 * />
 */
export const CompletedToggle: React.FC<CompletedToggleProps> = ({
  tasks, open, onOpenChange, onRestore, onDelete,
  externalSelection, onUpdateTask, onNavigateEdit, stateKey, handleRef,
  archivedGroups = [], renderArchivedSection,
}) => {
  const { t } = useTranslation()

  /*
    Die **Fokuszeit** steht hier nicht: `TaskList` holt sie sich für jede Liste
    selbst (3e). Zwei Abfragen über dieselben Kennungen wären zwei Wahrheiten.
  */
  const { loose, groups } = useMemo(() => {
    const toEntry = (task: CompletedToggleProps['tasks'][number]): TaskEntryData => ({
      // Das Extra wird ÜBER den gemerkten Entwurf gelegt, nicht in ihn
      // geschrieben: `taskToEntryData` merkt sich je `Task` in einer WeakMap.
      ...taskToEntryData(task),
      completedTime: formatCompletedTime(task.completedAt),
    })
    const grouped = new Set(archivedGroups.flatMap(g => g.tasks.map(task => task.id)))
    return {
      loose: tasks.filter(task => !grouped.has(task.id)).map(toEntry),
      groups: archivedGroups.map(g => ({ section: g.section, entries: g.tasks.map(toEntry) })),
    }
  }, [tasks, archivedGroups])

  if (tasks.length === 0 && archivedGroups.length === 0) return null

  // Fokus und Bearbeiten gelten je Liste nur, wenn die Zeile in ihr steht — die Auswahl selbst
  // ist die der Seite und gilt überall.
  const selectionFor = (entries: TaskEntryData[]): ExternalSelectionProps | undefined => {
    if (!externalSelection) return undefined
    const ids = new Set(entries.map(entry => entry.id))
    return {
      ...externalSelection,
      focusedTaskId: externalSelection.focusedTaskId && ids.has(externalSelection.focusedTaskId) ? externalSelection.focusedTaskId : null,
      editingTaskId: externalSelection.editingTaskId && ids.has(externalSelection.editingTaskId) ? externalSelection.editingTaskId : null,
    }
  }

  const renderList = (entries: TaskEntryData[], key: string | undefined, ref?: typeof handleRef) => (
    <TaskList
      tasks={entries}
      handleRef={ref}
      stateKey={key}
      menuKind="done"
      hidePlay
      externalSelection={groups.length > 0 ? selectionFor(entries) : externalSelection}
      onNavigateEdit={onNavigateEdit}
      onToggleComplete={ids => ids.forEach(id => onRestore(id))}
      onDelete={onDelete ? ids => ids.forEach(id => onDelete(id)) : undefined}
      onUpdateTask={onUpdateTask}
    />
  )

  const label = tasks.length > 0
    ? t(open ? 'taskList.completed.hide' : 'taskList.completed.show', { count: tasks.length })
    : t(open ? 'taskList.completed.hideArchived' : 'taskList.completed.showArchived')

  return (
    <div className={`completed-toggle${open ? ' completed-toggle--open' : ''}`}>
      <button
        type="button"
        className="completed-toggle__switch"
        onClick={() => onOpenChange(!open)}
        aria-expanded={open}
      >
        <span className="completed-toggle__label">{label}</span>
      </button>

      {open && (
        <div className="completed-toggle__list">
          {loose.length > 0 && renderList(loose, stateKey, handleRef)}
          {groups.map(group => (
            <div key={group.section.id} className="completed-toggle__group">
              {renderArchivedSection?.(group.section)}
              {group.entries.length > 0 && renderList(group.entries, stateKey && `${stateKey}-${group.section.id}`)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
